import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import { useRHFNestedFormContext } from '@openx/components-core/lib/Form/context/RHFNestedFormContext.tsx';
import { InfoTile } from '@openx/components-core/lib/InfoTile/InfoTile.tsx';
import {
  DEAL_PARTICIPANTS_SECTION_ID,
  type DealParticipant,
  dealFormField,
  RETARGETING_AQUA_VALIDATION_ERROR,
} from '@openx/types/deal.ts';
import { PackageFields, type PackageFormState } from '@openx/types/package.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const RetargetingAquaValidationAlert = ({ errorMsg }: { errorMsg?: string }) => {
  const { t } = useTranslation();
  const { parentForm, isNested } = useRHFNestedFormContext();

  const observedRetargeting = useWatch<PackageFormState>({
    name: `${PackageFields.TARGETING}.${TargetingFields.RETARGETING}`,
  });

  const observableDealParticipants: DealParticipant[] | undefined = useWatch({
    control: parentForm?.control,
    name: dealFormField.DEAL_PARTICIPANTS,
  });

  const snapshotRef = useRef<{ retargeting?: string; participants?: string }>({});

  useEffect(() => {
    if (!isNested || !parentForm) {
      return;
    }

    const retargetingString = JSON.stringify(observedRetargeting ?? null);
    const participantsString = JSON.stringify(observableDealParticipants ?? null);
    const snapshot = snapshotRef.current;

    if (retargetingString === snapshot.retargeting && participantsString === snapshot.participants) {
      return;
    }

    snapshotRef.current = { retargeting: retargetingString, participants: participantsString };
    parentForm.trigger(dealFormField.METADATA);
  }, [observedRetargeting, observableDealParticipants, isNested, parentForm]);

  const fieldState = parentForm?.getFieldState(dealFormField.METADATA);
  const message = String(fieldState?.error?.message ?? errorMsg ?? '');
  const showAlert = !!fieldState?.invalid && message.startsWith(RETARGETING_AQUA_VALIDATION_ERROR);

  if (!showAlert) {
    return null;
  }

  const handleGoToDsps = () => {
    document.getElementById(DEAL_PARTICIPANTS_SECTION_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <InfoTile
      data-test="retargeting-aqua-validation-alert"
      severity="error"
      icon={<ErrorOutlineIcon />}
      item={{
        title: t('Retargeting'),
        body: t('Aqua DSP must be selected when Retargeting segment applied to targeting'),
      }}
      secondaryAction={{ onAction: handleGoToDsps, button: t('Go to DSPs') }}
    />
  );
};
